// Výchozí obsah detail panelu dodaný root agendou (list-detail)

import { useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/lib/utils';
import { DetailZoneOutletContext } from './detailZoneOutletContext';

export interface DetailZoneOutletProps {
  /** Skryje obsah, ale nechá ho připojený — drží si stav i pozici scrollu. */
  readonly hidden?: boolean;
  readonly children: ReactNode;
}

/**
 * Výchozí obsah detail panelu list-detail rootu (workspace, prázdný stav, náhled).
 * Vrstva se `zone: 'detail'` (výběr v listu) ho vždy překryje.
 *
 * Obsah zůstává PŘIPOJENÝ i když je překrytý nebo skrytý — stejně jako shell netlumený root
 * neodmountovává, jen ho utlumí. Root tak drží stav svého workspace (rozpracovaný pohled, pozici
 * scrollu) i po odskoku do detailu.
 *
 * POZOR: skrývá se `visibility: hidden` (Tailwind `invisible`), NIKDY `display: none`.
 * `display: none` zruší layout box, takže prohlížeč resetuje `scrollTop` scrollovatelných potomků
 * na 0 a `offsetTop`/`getBoundingClientRect` uvnitř spadnou na nulu — tedy přesně to, co má tenhle
 * outlet zachovat. `inert` k tomu doplní blokaci fokusu i pointer eventů.
 */
export function DetailZoneOutlet({ hidden = false, children }: DetailZoneOutletProps) {
  const { target, covered } = useContext(DetailZoneOutletContext);
  if (!target) return null;

  const off = hidden || covered;

  return createPortal(
    <div className={cn('h-full', off && 'invisible')} inert={off}>
      {children}
    </div>,
    target
  );
}
